import { FloatingPortal } from "@floating-ui/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { styled } from "styled-components";
import { Snackbar } from "./snackbar";

const snackbarDurationMap: Record<
  NonNullable<SnackbarItem["length"]>,
  number
> = {
  s: 2500,
  m: 4000,
  l: 6000,
};

const getSnackbarDuration = (item: SnackbarItem): number => {
  return snackbarDurationMap[item.length ?? "s"];
};

type SnackbarItem = {
  text: string;
  isError?: boolean;
  length?: "s" | "m" | "l";
};

type SnackbarContextType = {
  enqueueSnackbar?: (item: SnackbarItem) => void;
};

const SnackbarContext = createContext<SnackbarContextType>({
  enqueueSnackbar: undefined,
});

export const useSnackbar = (): Required<
  Pick<SnackbarContextType, "enqueueSnackbar">
> => {
  const { enqueueSnackbar } = useContext(SnackbarContext);

  if (!enqueueSnackbar) {
    return { enqueueSnackbar: () => null };
  }

  return { enqueueSnackbar };
};

type Props = {
  children?: React.ReactNode;
};

export const SnackbarProvider: React.FC<Props> = ({ children }) => {
  const [snackbarQueue, setSnackbarQueue] = useState<SnackbarItem[]>([]);
  const snackbarRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  useEffect(() => {
    if (snackbarQueue.length > 0 && !snackbarRef.current) {
      snackbarRef.current = setTimeout(() => {
        const updatedQueue = [...snackbarQueue];
        updatedQueue.shift();
        snackbarRef.current = undefined;
        setSnackbarQueue(updatedQueue);
      }, getSnackbarDuration(snackbarQueue[0]));
    }

    return () => {
      clearTimeout(snackbarRef.current);
      snackbarRef.current = undefined;
    };
  }, [snackbarQueue]);

  const enqueueSnackbar = useCallback(
    (item: SnackbarItem) => {
      setSnackbarQueue((oldQueue) =>
        oldQueue.map((snackbar) => snackbar.text).includes(item.text)
          ? oldQueue
          : [...oldQueue, item],
      );
    },
    [setSnackbarQueue],
  );

  return (
    <SnackbarContext.Provider value={{ enqueueSnackbar }}>
      <FloatingPortal>
        <SnackbarContainer>
          {snackbarQueue.map((snackbar) => (
            <Snackbar
              key={snackbar.text}
              text={snackbar.text}
              isError={snackbar.isError ?? false}
            />
          ))}
        </SnackbarContainer>
      </FloatingPortal>
      {children}
    </SnackbarContext.Provider>
  );
};

const SnackbarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding-top: ${({ theme }) => theme.MARGIN.MD};
  display: flex;
  flex-direction: column;
  gap: 1px;
  pointer-events: none;
  // Must be above MUI Dialog (z-index: 1300) so snackbars remain visible
  // when triggered from inside modals. Matches MUI's own snackbar z-index.
  z-index: 1400;
`;
